/**
 * Build Client Connect invitation preview copy and media descriptors from form state.
 */

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim() !== '';
}

export function isLocalUploadFile(value) {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const tag = Object.prototype.toString.call(value);
  return tag === '[object File]' || tag === '[object Blob]';
}

function isAbsoluteMediaUrl(value) {
  const v = String(value).trim();
  return (
    v.startsWith('http://') ||
    v.startsWith('https://') ||
    v.startsWith('//') ||
    v.startsWith('/') ||
    v.startsWith('blob:') ||
    v.startsWith('data:')
  );
}

/** True when a string is a URL or storage path/filename, not display metadata (name, size, mime type, id). */
function looksLikeResolvableMediaPath(value) {
  if (!isNonEmptyString(value)) {
    return false;
  }
  const v = value.trim();
  if (isAbsoluteMediaUrl(v)) {
    return true;
  }
  if (/^(image|video|audio|application)\//i.test(v)) {
    return false;
  }
  if (v.includes('/')) {
    return true;
  }
  return /\.(jpe?g|png|gif|webp|svg|bmp|mp4|webm|mov|avi|m4v|pdf)(\?|#|$)/i.test(v);
}

export function toDisplayableMediaUrl(value) {
  if (!isNonEmptyString(value)) {
    return null;
  }
  const trimmed = value.trim();
  if (isAbsoluteMediaUrl(trimmed)) {
    return trimmed;
  }
  if (!looksLikeResolvableMediaPath(trimmed)) {
    return null;
  }

  const path = trimmed.replace(/^\//, '');

  const imageBase = process.env.REACT_APP_IMAGE_BASE_URL;
  if (imageBase) {
    const base = String(imageBase).replace(/\/$/, '');
    return `${base}/${path}`;
  }

  const deployBase = process.env.REACT_APP_URL;
  if (deployBase) {
    const base = String(deployBase).replace(/\/$/, '');
    if (path.startsWith('resources/')) {
      return `${base}/${path}`;
    }
    return `${base}/resources/images/${path}`;
  }

  return null;
}

function collectMediaCandidates(media) {
  if (typeof media !== 'object' || !media) {
    return [];
  }
  const keys = ['fileUrl', 'file_url', 'url', 'logo_url', 'src', 'image', 'thumbnail', 'path', 'video', 'logo', 'file'];
  return keys.map((key) => media[key]).filter((v) => isNonEmptyString(v) && looksLikeResolvableMediaPath(v));
}

export function resolveClientConnectLogoPreviewSrc(logo) {
  if (!logo || isLocalUploadFile(logo)) {
    return null;
  }

  if (typeof logo === 'string') {
    return toDisplayableMediaUrl(logo);
  }

  if (typeof logo === 'object') {
    const resolved = collectMediaCandidates(logo)
      .map((candidate) => toDisplayableMediaUrl(candidate))
      .find(Boolean);
    if (resolved) {
      return resolved;
    }
  }

  return null;
}

export function resolveClientConnectIntroductionVideoSrc(introductionVideo) {
  if (!introductionVideo || isLocalUploadFile(introductionVideo)) {
    return null;
  }
  if (typeof introductionVideo === 'string') {
    return toDisplayableMediaUrl(introductionVideo);
  }
  if (typeof introductionVideo === 'object') {
    const resolved = collectMediaCandidates(introductionVideo)
      .map((candidate) => toDisplayableMediaUrl(candidate))
      .find(Boolean);
    if (resolved) {
      return resolved;
    }
  }
  return null;
}

export function resolveClientConnectMaterialFileUrl(content) {
  if (!content || content.file instanceof File || content.file instanceof Blob) {
    return null;
  }

  const candidates = [content.dataUrl, content.fileUrl, content.file_url, content.file].filter((value) =>
    isNonEmptyString(value)
  );

  const resolvedCandidate = candidates
    .map((candidate) => {
      const trimmed = candidate.trim();
      if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
        return trimmed;
      }
      return toDisplayableMediaUrl(trimmed);
    })
    .find(Boolean);

  return resolvedCandidate || null;
}

const DOWNLOAD_BLOB_REVOKE_DELAY_MS = 1000;

/** Defer blob URL cleanup so anchor downloads can finish reading the blob. */
export function scheduleObjectUrlRevoke(objectUrl) {
  if (!objectUrl || !objectUrl.startsWith('blob:')) {
    return;
  }

  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, DOWNLOAD_BLOB_REVOKE_DELAY_MS);
}

/** Trigger a browser download (fetch remote URLs as blob so `download` is honored). */
export async function downloadMaterialFile(fileUrl, fileName = 'download') {
  if (!fileUrl) {
    return;
  }

  const safeName = fileName || 'download';

  const triggerDownload = (href) => {
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = safeName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  if (fileUrl.startsWith('blob:') || fileUrl.startsWith('data:')) {
    triggerDownload(fileUrl);
    return;
  }

  const response = await fetch(fileUrl, { method: 'GET' });
  if (!response.ok) {
    throw new Error('Failed to download file');
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  triggerDownload(objectUrl);
  scheduleObjectUrlRevoke(objectUrl);
}

export function hasClientConnectLogo(logo) {
  if (logo == null || logo === false) {
    return false;
  }
  if (isLocalUploadFile(logo)) {
    return true;
  }
  if (typeof logo === 'string') {
    return logo.trim() !== '';
  }
  if (typeof logo === 'object') {
    return true;
  }
  return false;
}

/** Same base as survey list copy-link (`src/views/app/survey/list/index.jsx`). */
export function getSurveyLoginBaseUrl() {
  const env = process.env.REACT_APP_FRONT_RATING_URL;
  if (env != null && String(env).trim() !== '') {
    return String(env).replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return '';
}

/** Public survey taker URL: `/survey/login?survey_id=…` (opens in new tab from preview). */
export function getSurveyLoginUrl(surveyId) {
  if (surveyId == null || surveyId === '') return '';
  const base = getSurveyLoginBaseUrl();
  if (!base) return '';
  return `${base}/survey/login?survey_id=${encodeURIComponent(String(surveyId))}`;
}

/** Per-recipient Client Connect URL (matches sf-api invite email `surveyUrl`). */
export function getClientConnectRecipientSurveyUrl({ surveyId, inviteId, email }) {
  if (surveyId == null || surveyId === '' || inviteId == null || inviteId === '' || !email) {
    return '';
  }
  const base = getSurveyLoginBaseUrl();
  if (!base) return '';
  const params = new URLSearchParams({
    survey_id: String(surveyId),
    audit: String(inviteId),
    email: String(email),
  });
  return `${base}/survey/login?${params.toString()}`;
}

export function resolveLoginCompanyName() {
  try {
    const raw = localStorage.getItem('loginUser');
    if (!raw) return '';
    const login = JSON.parse(raw);
    return String(
      login?.companyInfo?.name || login?.companyInfo?.companyName || login?.user?.company_name || ''
    ).trim();
  } catch {
    return '';
  }
}

/**
 * Survey display name for the selected survey id (matches InputSurvey / dropdown).
 * Compares ids as strings so ObjectId vs string never misses.
 */
export function resolveSurveyLabel(selectedSurveyId, surveyOptions) {
  if (selectedSurveyId == null || selectedSurveyId === '') return '';
  if (!Array.isArray(surveyOptions) || surveyOptions.length === 0) return '';
  const needle = String(selectedSurveyId);
  const opt = surveyOptions.find((s) => String(s.value) === needle);
  return String(opt?.label || '').trim();
}

export function isMaterialRowFilled(row) {
  if (!row || typeof row !== 'object') return false;
  return Boolean(row.file || (row.link && String(row.link).trim()) || String(row.name || '').trim());
}

/** @returns {{ key: string, name: string, url?: string, file?: File }[]} */
export function collectPdfAttachments(clientContent) {
  const rows = (clientContent || []).filter(isMaterialRowFilled);
  const out = [];
  rows.forEach((row, index) => {
    if (row.file instanceof File && row.file.type === 'application/pdf') {
      out.push({
        key: String(row.id || `pdf-${index}`),
        name: row.file.name || 'document.pdf',
        file: row.file,
      });
    } else if (row.link && String(row.link).trim().toLowerCase().endsWith('.pdf')) {
      const nameFromRow = String(row.name || '').trim();
      const name =
        nameFromRow ||
        (() => {
          try {
            return decodeURIComponent(new URL(row.link).pathname.split('/').pop() || 'document.pdf');
          } catch {
            return 'document.pdf';
          }
        })();
      out.push({
        key: String(row.id || `pdf-link-${index}`),
        name,
        url: String(row.link).trim(),
      });
    }
  });
  return out;
}

/**
 * Gallery descriptors: intro video first (if any), then material videos, then images.
 * Each item is either { key, kind, file: File } or { key, kind, src: string }.
 */
export function buildGalleryDescriptors(introductionVideo, clientContent) {
  const items = [];

  if (introductionVideo instanceof File && introductionVideo.type.startsWith('video/')) {
    items.push({ key: 'intro-file', kind: 'video', file: introductionVideo });
  } else {
    const introSrc = resolveClientConnectIntroductionVideoSrc(introductionVideo);
    if (introSrc) {
      items.push({
        key: 'intro-url',
        kind: 'video',
        src: introSrc,
      });
    }
  }

  const rows = (clientContent || []).filter(isMaterialRowFilled);
  rows.forEach((row, index) => {
    const key = String(row.id || `mat-${index}`);
    if (row.file instanceof File) {
      if (row.file.type.startsWith('video/')) {
        items.push({ key: `${key}-vid`, kind: 'video', file: row.file });
      } else if (row.file.type.startsWith('image/')) {
        items.push({ key: `${key}-img`, kind: 'image', file: row.file });
      }
    } else if (row.link && String(row.link).trim()) {
      const link = String(row.link).trim();
      if (/\.(png|jpg|jpeg|gif|webp)(\?|#|$)/i.test(link)) {
        items.push({ key: `${key}-link`, kind: 'image', src: link });
      }
    } else {
      const inline =
        (row.dataUrl && String(row.dataUrl).trim()) || (row.displayImage && String(row.displayImage).trim());
      if (inline) {
        items.push({ key: `${key}-inline`, kind: 'image', src: inline });
      }
    }
  });

  if (items.length === 0) {
    return { wide: null, sides: [] };
  }
  return { wide: items[0], sides: items.slice(1, 3) };
}
